
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Check, AlertCircle, Clock } from "lucide-react";
import { EmailService } from "@/services/EmailService";

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

export const ForgotPasswordForm = ({ onBackToLogin }: ForgotPasswordFormProps) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);
  const [rateLimitSeconds, setRateLimitSeconds] = useState(0);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Get the site URL for proper redirection
      const siteUrl = window.location.origin;
      
      // Generate a reset token via Supabase
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${siteUrl}/auth?mode=reset-password`,
      });
      
      if (error) {
        // Check for rate limit error specifically
        if (error.message.includes("security purposes") && error.message.includes("seconds")) {
          // Extract the seconds from the error message
          const secondsMatch = error.message.match(/(\d+) seconds/);
          const seconds = secondsMatch ? parseInt(secondsMatch[1]) : 60;
          
          setRateLimited(true);
          setRateLimitSeconds(seconds);
          setLoading(false);
          
          toast({
            title: "Too many attempts",
            description: `For security purposes, please wait ${seconds} seconds before trying again.`,
            variant: "destructive",
          });
          
          return;
        }
        throw error;
      }
      
      console.log("Password reset token generated");
      
      // The reset link will go to our auth page with the reset-password mode
      const resetLink = `${siteUrl}/auth?mode=reset-password`;
      
      // Send our custom MiniMakes branded email with the reset link
      const emailSent = await EmailService.sendPasswordResetEmail(email, resetLink);
      
      if (!emailSent) {
        throw new Error("Failed to send password reset email");
      }
      
      // Display success message
      setSubmitted(true);
      toast({
        title: "Check your email!",
        description: "We've sent password reset instructions to your email.",
        variant: "default",
      });
    } catch (error) {
      console.error("Reset password error:", error);
      toast({
        title: "Error",
        description: (error as Error).message,
        variant: "destructive",
      });
      setSubmitted(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-center">Reset Password</h2>
      
      {submitted ? (
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded-md flex items-start">
            <Check className="text-green-500 mt-1 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-green-800">Check your email</h3>
              <p className="text-sm text-green-700 mt-1">
                We've sent instructions to reset your password to {email}.
                Please check your inbox and follow the instructions.
              </p>
            </div>
          </div>
          <p className="text-center text-muted-foreground text-sm">
            Didn't receive an email? Check your spam folder or try again.
          </p>
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setEmail("");
              }}
            >
              Try Again
            </Button>
            <Button
              type="button"
              onClick={onBackToLogin}
            >
              Back to Login
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-center text-muted-foreground">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading || rateLimited}
              className="w-full"
            />
            
            {rateLimited ? (
              <div className="bg-amber-50 p-3 rounded-md flex items-start">
                <Clock className="text-amber-500 mt-0.5 mr-2 h-4 w-4 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  For security purposes, please wait before requesting another password reset.
                  You can try again in {rateLimitSeconds} seconds.
                </p>
              </div>
            ) : (
              <div className="bg-blue-50 p-3 rounded-md flex items-start">
                <AlertCircle className="text-blue-500 mt-0.5 mr-2 h-4 w-4 flex-shrink-0" />
                <p className="text-xs text-blue-700">
                  You will receive an email with a link to reset your password.
                </p>
              </div>
            )}
          </div>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || rateLimited}
          >
            {loading ? "Sending..." : "Send Reset Instructions"}
          </Button>
          <div className="text-center">
            <Button
              type="button"
              variant="link"
              onClick={onBackToLogin}
              className="text-sm"
            >
              Back to Login
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
